// HostDashboard.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { auth } from "./firebase.js";
import { onAuthStateChanged } from "firebase/auth";
import { getStorage, ref, getMetadata, getBlob } from "firebase/storage";
import Papa from "papaparse";

/* ============================ Config / Fields ============================ */
const FIELD = {
  start: "start_time",
  end: "end_time",
  carName: "vehicle_name",
  carId: "vehicle_id",
  earnings: "host_earnings",
};

/* =============================== Utilities ============================== */
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const iso = (d) => d.toISOString().slice(0, 10);
const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const monthLabel = (m) =>
  [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][m];
const fmtInt = (n) => (Number.isFinite(n) ? n.toLocaleString() : "—");
const fmtUsd = (n) =>
  Number.isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—";
const fmtPct = (n) => (Number.isFinite(n) ? `${n.toFixed(0)}%` : "—");

/* ========================= Plate Extraction Helpers ====================== */
const US_STATES = new Set([
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
]);

const normalizePlate = (v) =>
  String(v || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

/** Extract from vehicle name only (handles “… (CA #9GRH477)”, etc.). */
function extractPlateFromVehicleName(name) {
  if (!name) return "";
  const s = String(name);
  const groups = [...s.matchAll(/\(([^)]+)\)/g)];
  for (let gi = groups.length - 1; gi >= 0; gi--) {
    const inside = groups[gi][1].toUpperCase();
    const tokens = inside
      .split(/[\s,]+/)
      .filter(Boolean)
      .map((t) => t.replace(/^#/, ""));
    for (let ti = tokens.length - 1; ti >= 0; ti--) {
      const raw = tokens[ti];
      const prev = tokens[ti - 1] || "";
      const tok = normalizePlate(raw);
      if (!tok) continue;
      const okLen = tok.length >= 4 && tok.length <= 8;
      const hasDigit = /\d/.test(tok);
      const prevIsState = US_STATES.has(normalizePlate(prev));
      if ((hasDigit && okLen) || (okLen && prevIsState)) return tok;
    }
  }
  const hashHit = s.match(/#\s*([A-Z0-9]{4,8})/i);
  if (hashHit) return normalizePlate(hashHit[1]);
  const statePlate = s.match(
    /\b(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC)\b\W*([A-Z0-9]{4,8})/i
  );
  if (statePlate) return normalizePlate(statePlate[2]);
  return "";
}

/** Scan every string cell in the row for a plate pattern. */
function extractPlateFromAnyCell(row) {
  const statePlateRe =
    /\b(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC)\b\W*([A-Z0-9]{4,8})/i;
  const hashPlateRe = /#\s*([A-Z0-9]{4,8})/i;
  for (const k in row) {
    const v = row[k];
    if (v == null) continue;
    const s = String(v);
    if (s.length > 400) continue;
    const m1 = s.match(statePlateRe);
    if (m1) return normalizePlate(m1[2]);
    const m2 = s.match(hashPlateRe);
    if (m2) return normalizePlate(m2[1]);
    const parenGroups = [...s.matchAll(/\(([^)]+)\)/g)];
    for (let gi = parenGroups.length - 1; gi >= 0; gi--) {
      const inside = parenGroups[gi][1].toUpperCase();
      const tokens = inside
        .split(/[\s,]+/)
        .filter(Boolean)
        .map((t) => t.replace(/^#/, ""));
      for (let ti = tokens.length - 1; ti >= 0; ti--) {
        const raw = tokens[ti];
        const prev = tokens[ti - 1] || "";
        const tok = normalizePlate(raw);
        if (!tok) continue;
        const okLen = tok.length >= 4 && tok.length <= 8;
        const hasDigit = /\d/.test(tok);
        const prevIsState = US_STATES.has(normalizePlate(prev));
        if ((hasDigit && okLen) || (okLen && prevIsState)) return tok;
      }
    }
  }
  return "";
}

/* ============================= Booking Utilities ======================== */
function bookedDateSetForRow(row) {
  const s = new Date(row[FIELD.start]);
  const e = new Date(row[FIELD.end]);
  if (isNaN(s) || isNaN(e)) return new Set();
  const out = new Set();
  let cur = startOfDay(s);
  const last = startOfDay(e);
  while (cur <= last) {
    out.add(iso(cur));
    cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
  }
  return out;
}

/* ============================ Aggregation Core =========================== */
function buildAggregates(rows) {
  const cars = new Map();
  const years = new Set();
  const byMonth = new Map();
  const yearly = new Map();

  const carKeyOf = (r) =>
    `${r[FIELD.carId] ?? "unknown"}||${r[FIELD.carName] ?? ""}`;
  const ensureCar = (k, name, id) => {
    if (!cars.has(k)) cars.set(k, { id, name: name || "(Unnamed car)" });
  };
  const ensureMonthBucket = (carKey, y, m) => {
    if (!byMonth.has(carKey)) byMonth.set(carKey, new Map());
    const yMap = byMonth.get(carKey);
    if (!yMap.has(y))
      yMap.set(
        y,
        Array.from({ length: 12 }, () => null)
      );
    const arr = yMap.get(y);
    if (!arr[m]) arr[m] = { trips: 0, bookedDates: new Set(), earnings: 0 };
    return arr[m];
  };
  const ensureYearBucket = (carKey, y) => {
    if (!yearly.has(carKey)) yearly.set(carKey, new Map());
    const yMap = yearly.get(carKey);
    if (!yMap.has(y))
      yMap.set(y, { trips: 0, bookedDates: new Set(), earnings: 0 });
    return yMap.get(y);
  };

  for (const row of rows || []) {
    const s = new Date(row[FIELD.start]);
    const e = new Date(row[FIELD.end]);
    if (isNaN(s) || isNaN(e)) continue;

    const sY = s.getFullYear();
    years.add(sY);

    const name = row[FIELD.carName];
    const id = row[FIELD.carId] ?? "unknown";
    const key = carKeyOf(row);
    ensureCar(key, name, id);

    const bset = bookedDateSetForRow(row);
    const earn = Number(row[FIELD.earnings]) || 0;
    const perDay = bset.size ? earn / bset.size : 0;

    // Count the trip in the start month/year
    const mStart = ensureMonthBucket(key, sY, s.getMonth());
    mStart.trips += 1;
    const yStart = ensureYearBucket(key, sY);
    yStart.trips += 1;

    // Spread booked days + earnings across actual dates
    bset.forEach((isoDay) => {
      const [yy, mm] = isoDay.split("-").map((n) => parseInt(n, 10));
      const y = yy,
        m = mm - 1;
      years.add(y);
      const mB = ensureMonthBucket(key, y, m);
      mB.earnings += perDay;
      mB.bookedDates.add(isoDay);

      const yB = ensureYearBucket(key, y);
      yB.earnings += perDay;
      yB.bookedDates.add(isoDay);
    });
  }

  // Finalize monthly metrics
  for (const [, yMap] of byMonth.entries()) {
    for (const [y, arr] of yMap.entries()) {
      for (let m = 0; m < 12; m++) {
        const b = arr[m];
        if (!b) {
          arr[m] = {
            month: m,
            trips: 0,
            bookedDays: 0,
            earnings: 0,
            demandPct: 0,
            avgDaily: 0,
          };
          continue;
        }
        const bookedDays = b.bookedDates.size;
        const dim = daysInMonth(y, m);
        const demandPct = dim ? (bookedDays / dim) * 100 : 0;
        const avgDaily = bookedDays ? b.earnings / bookedDays : 0;
        arr[m] = {
          month: m,
          trips: b.trips,
          bookedDays,
          earnings: b.earnings,
          demandPct,
          avgDaily,
        };
      }
    }
  }

  // Finalize yearly metrics
  for (const [, yMap] of yearly.entries()) {
    for (const [y, b] of yMap.entries()) {
      const bookedDays = b.bookedDates.size;
      const dim = Array.from({ length: 12 }).reduce(
        (s, _, m) => s + daysInMonth(y, m),
        0
      );
      const demandPct = dim ? (bookedDays / dim) * 100 : 0;
      const avgDaily = bookedDays ? b.earnings / bookedDays : 0;
      yMap.set(y, {
        year: y,
        trips: b.trips,
        bookedDays,
        earnings: b.earnings,
        demandPct,
        avgDaily,
      });
    }
  }

  // "__ALL__" aggregate
  const ALL = "__ALL__";
  cars.set(ALL, { id: ALL, name: "All Cars" });

  const allByMonth = new Map();
  for (const y of years) {
    const months = Array.from({ length: 12 }, (_, m) => ({
      month: m,
      trips: 0,
      bookedDays: 0,
      earnings: 0,
    }));
    const fleetSets = Array.from({ length: 12 }, () => new Set());
    for (const [carKey, carY] of byMonth.entries()) {
      const arr = carY.get(y);
      if (!arr) continue;
      arr.forEach((b, m) => {
        if (!b) return;
        months[m].trips += b.trips;
        months[m].bookedDays += b.bookedDays;
        months[m].earnings += b.earnings;
        if (b.bookedDays > 0 || b.trips > 0 || b.earnings > 0)
          fleetSets[m].add(carKey);
      });
    }
    months.forEach((b, m) => {
      const dim = daysInMonth(y, m);
      const fleet = Math.max(1, fleetSets[m].size);
      b.demandPct = dim ? (b.bookedDays / (dim * fleet)) * 100 : 0;
      b.avgDaily = b.bookedDays ? b.earnings / b.bookedDays : 0;
    });
    allByMonth.set(y, months);
  }
  byMonth.set(ALL, allByMonth);

  const allYearly = new Map();
  for (const y of years) {
    let trips = 0,
      bookedDays = 0,
      earnings = 0;
    const fleetYear = new Set();
    for (const [, carY] of yearly.entries()) {
      const b = carY.get(y);
      if (!b) continue;
      trips += b.trips;
      bookedDays += b.bookedDays;
      earnings += b.earnings;
      if (b.bookedDays > 0 || b.trips > 0 || b.earnings > 0)
        fleetYear.add(carY);
    }
    const dim = Array.from({ length: 12 }).reduce(
      (s, _, m) => s + daysInMonth(y, m),
      0
    );
    const demandPct = dim
      ? (bookedDays / (dim * Math.max(1, fleetYear.size))) * 100
      : 0;
    const avgDaily = bookedDays ? earnings / bookedDays : 0;
    allYearly.set(y, {
      year: y,
      trips,
      bookedDays,
      earnings,
      demandPct,
      avgDaily,
    });
  }
  yearly.set(ALL, allYearly);

  return {
    cars: Array.from(cars.entries()).map(([key, v]) => ({ key, ...v })),
    years: Array.from(years).sort((a, b) => b - a),
    byMonth,
    yearly,
  };
}

/* ============================= UI Pieces ================================ */
function KPI({ title, value }) {
  return (
    <div className="rounded-xl border p-4 bg-white shadow-sm">
      <div className="text-xs text-gray-500">{title}</div>
      <div className="text-xl font-semibold mt-1">{value ?? "—"}</div>
    </div>
  );
}
function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 font-medium ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 ${className}`}>{children}</td>;
}

/* ===================== Fleet Performance Breakdown ====================== */
function FleetBreakdown({ rows, year, carKeySelected }) {
  // Filter rows by selection (car + year)
  const filtered = useMemo(() => {
    let out = rows;
    if (carKeySelected && carKeySelected !== "__ALL__") {
      // carKey format: `${vehicle_id}||${vehicle_name}`
      const [vid, vname] = carKeySelected.split("||");
      out = out.filter(
        (r) =>
          String(r[FIELD.carId] ?? "unknown") === vid &&
          String(r[FIELD.carName] ?? "") === vname
      );
    }
    if (year !== "ALL") {
      out = out.filter((r) => {
        const s = new Date(r[FIELD.start]);
        const e = new Date(r[FIELD.end]);
        return (
          !isNaN(s) &&
          !isNaN(e) &&
          (s.getFullYear() === year || e.getFullYear() === year)
        );
      });
    }
    return out;
  }, [rows, year, carKeySelected]);

  const breakdown = useMemo(() => {
    // Aggregate by vehicle_name (+ vehicle_id for clarity)
    const byCar = new Map();

    for (const r of filtered) {
      const name = r[FIELD.carName] || "(Unnamed car)";
      const vid = r[FIELD.carId] || "unknown";
      const key = `${vid}||${name}`;

      const bset = bookedDateSetForRow(r);
      const earnings = Number(r[FIELD.earnings]) || 0;

      if (!byCar.has(key)) {
        byCar.set(key, {
          name,
          vehicle_id: vid,
          trips: 0,
          bookedDays: 0,
          earnings: 0,
          yearsSeen: new Set(),
        });
      }
      const slot = byCar.get(key);
      slot.trips += 1;
      slot.bookedDays += bset.size;
      slot.earnings += earnings;
      // Track years this row spans
      for (const d of bset) {
        slot.yearsSeen.add(Number(d.slice(0, 4)));
      }
    }

    // Denominators for demand:
    // - if year is numeric: calendar days in that year
    // - if "ALL": sum of calendar days across all years seen for that car
    const result = [];
    for (const [, v] of byCar.entries()) {
      let daysDenom = 0;
      if (year === "ALL") {
        const yearsArr = Array.from(v.yearsSeen);
        for (const y of yearsArr) {
          daysDenom += Array.from({ length: 12 }).reduce(
            (s, _, m) => s + daysInMonth(y, m),
            0
          );
        }
      } else {
        daysDenom = Array.from({ length: 12 }).reduce(
          (s, _, m) => s + daysInMonth(year, m),
          0
        );
      }
      const demandPct = daysDenom ? (v.bookedDays / daysDenom) * 100 : 0;
      const avgDaily = v.bookedDays ? v.earnings / v.bookedDays : 0;
      result.push({
        name: v.name,
        vehicle_id: v.vehicle_id,
        trips: v.trips,
        bookedDays: v.bookedDays,
        earnings: v.earnings,
        demandPct,
        avgDaily,
      });
    }

    // % of Fleet Earnings
    const fleetTotal = result.reduce((s, r) => s + r.earnings, 0);
    result.forEach((r) => {
      r.fleetPct = fleetTotal ? (r.earnings / fleetTotal) * 100 : 0;
    });

    // Sort by earnings desc
    return result.sort((a, b) => b.earnings - a.earnings);
  }, [filtered, year]);

  if (!breakdown.length) return null;

  return (
    <div className="mt-10">
      <h3 className="text-lg font-semibold mb-3">
        🚗 Fleet Performance Breakdown
      </h3>
      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <Th>Car</Th>
              <Th>Plate / ID</Th>
              <Th className="text-right">Trips</Th>
              <Th className="text-right">Booked Days</Th>
              <Th className="text-right">Demand</Th>
              <Th className="text-right">Earnings</Th>
              <Th className="text-right">Avg Daily Rate</Th>
              <Th className="text-right">% Fleet $</Th>
            </tr>
          </thead>
          <tbody>
            {breakdown.map((r, i) => (
              <tr key={`${r.vehicle_id}-${i}`} className="border-t">
                <Td>{r.name}</Td>
                <Td>{r.vehicle_id || "—"}</Td>
                <Td className="text-right">{fmtInt(r.trips)}</Td>
                <Td className="text-right">{fmtInt(r.bookedDays)}</Td>
                <Td className="text-right">{fmtPct(r.demandPct)}</Td>
                <Td className="text-right">{fmtUsd(r.earnings)}</Td>
                <Td className="text-right">{fmtUsd(r.avgDaily)}</Td>
                <Td className="text-right">{fmtPct(r.fleetPct)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Demand% = Booked days ÷ Calendar days{" "}
        {year === "ALL" ? "across all years for this car" : `in ${year}`}. Avg
        Daily Rate = Earnings ÷ Booked days.
      </p>
    </div>
  );
}

/* ============================= Dashboard View =========================== */
function Dashboard({ rows }) {
  const { cars, years, byMonth, yearly } = useMemo(
    () => buildAggregates(rows || []),
    [rows]
  );

  const defaultCar =
    cars.find((c) => c.key === "__ALL__")?.key ?? cars[0]?.key ?? "__ALL__";
  const defaultYear = years[0];
  const yearOptions = useMemo(() => ["ALL", ...years], [years]);

  const [carKey, setCarKey] = useState(defaultCar);
  const [year, setYear] = useState(defaultYear);

  // Lifetime combiners
  function combineMonthsAllYears(selCarKey) {
    // For a single car, the simple (unadjusted) capacity by calendar months works fine
    if (selCarKey !== "__ALL__") {
      const yMap = byMonth.get(selCarKey);
      if (!yMap)
        return Array.from({ length: 12 }, (_, m) => ({
          month: m,
          trips: 0,
          bookedDays: 0,
          earnings: 0,
          demandPct: 0,
          avgDaily: 0,
        }));

      const months = Array.from({ length: 12 }, (_, m) => ({
        month: m,
        trips: 0,
        bookedDays: 0,
        earnings: 0,
      }));
      const capacityDays = Array.from({ length: 12 }, () => 0);

      for (const [y, arr] of yMap.entries()) {
        for (let m = 0; m < 12; m++) {
          const b = arr[m];
          if (!b) continue;
          months[m].trips += b.trips || 0;
          months[m].bookedDays += b.bookedDays || 0;
          months[m].earnings += b.earnings || 0;
          capacityDays[m] += daysInMonth(y, m);
        }
      }

      return months.map((b, m) => {
        const dim = capacityDays[m] || 0;
        const demandPct = dim ? (b.bookedDays / dim) * 100 : 0;
        const avgDaily = b.bookedDays ? b.earnings / b.bookedDays : 0;
        return { ...b, demandPct, avgDaily };
      });
    }

    // ⬇️ Fleet-adjusted lifetime for "All Cars"
    // Denominator = Σ_y Σ_m [ daysInMonth(y,m) * activeFleetCount(y,m) ]
    // where activeFleetCount(y,m) = number of cars with any activity in (y,m)
    const months = Array.from({ length: 12 }, (_, m) => ({
      month: m,
      trips: 0,
      bookedDays: 0,
      earnings: 0,
    }));
    const capacityDays = Array.from({ length: 12 }, () => 0);

    // Consider all individual cars (exclude the synthesized "__ALL__" entry)
    const carEntries = Array.from(byMonth.entries()).filter(
      ([key]) => key !== "__ALL__"
    );

    // Collect all years represented across cars
    const yearsSet = new Set();
    for (const [, yMap] of carEntries) {
      for (const y of yMap.keys()) yearsSet.add(y);
    }

    for (const y of yearsSet) {
      const activeCarsPerMonth = Array.from({ length: 12 }, () => 0);

      for (const [, yMap] of carEntries) {
        const arr = yMap.get(y);
        if (!arr) continue;

        for (let m = 0; m < 12; m++) {
          const b = arr[m];
          if (!b) continue;

          // Accumulate fleet totals
          months[m].trips += b.trips || 0;
          months[m].bookedDays += b.bookedDays || 0;
          months[m].earnings += b.earnings || 0;

          // Mark this car active in (y,m) if any activity is present
          if (
            (b.bookedDays || 0) > 0 ||
            (b.trips || 0) > 0 ||
            (b.earnings || 0) > 0
          ) {
            activeCarsPerMonth[m] += 1;
          }
        }
      }

      for (let m = 0; m < 12; m++) {
        const fleet = Math.max(1, activeCarsPerMonth[m]);
        capacityDays[m] += daysInMonth(y, m) * fleet;
      }
    }

    return months.map((b, m) => {
      const dim = capacityDays[m] || 0;
      const demandPct = dim ? (b.bookedDays / dim) * 100 : 0;
      const avgDaily = b.bookedDays ? b.earnings / b.bookedDays : 0;
      return { ...b, demandPct, avgDaily };
    });
  }

  function combineYearAllYears(selCarKey) {
    // Single car: simple lifetime totals over calendar days
    if (selCarKey !== "__ALL__") {
      const yMap = yearly.get(selCarKey);
      if (!yMap)
        return {
          year: "ALL",
          trips: 0,
          bookedDays: 0,
          earnings: 0,
          demandPct: 0,
          avgDaily: 0,
        };

      let trips = 0,
        bookedDays = 0,
        earnings = 0,
        totalDays = 0;
      for (const [y, b] of yMap.entries()) {
        trips += b.trips || 0;
        bookedDays += b.bookedDays || 0;
        earnings += b.earnings || 0;
        totalDays += Array.from({ length: 12 }).reduce(
          (s, _, m) => s + daysInMonth(y, m),
          0
        );
      }
      const demandPct = totalDays ? (bookedDays / totalDays) * 100 : 0;
      const avgDaily = bookedDays ? earnings / bookedDays : 0;
      return { year: "ALL", trips, bookedDays, earnings, demandPct, avgDaily };
    }

    // ⬇️ Fleet-adjusted lifetime for "All Cars"
    // Use byMonth for both totals and capacity so we can account for active fleet per month
    const carEntries = Array.from(byMonth.entries()).filter(
      ([key]) => key !== "__ALL__"
    );
    const yearsSet = new Set();
    for (const [, yMap] of carEntries) {
      for (const y of yMap.keys()) yearsSet.add(y);
    }

    let trips = 0,
      bookedDays = 0,
      earnings = 0,
      capacityTotal = 0;

    for (const y of yearsSet) {
      const activeCarsPerMonth = Array.from({ length: 12 }, () => 0);

      for (const [, yMap] of carEntries) {
        const arr = yMap.get(y);
        if (!arr) continue;

        for (let m = 0; m < 12; m++) {
          const b = arr[m];
          if (!b) continue;

          trips += b.trips || 0;
          bookedDays += b.bookedDays || 0;
          earnings += b.earnings || 0;

          if (
            (b.bookedDays || 0) > 0 ||
            (b.trips || 0) > 0 ||
            (b.earnings || 0) > 0
          ) {
            activeCarsPerMonth[m] += 1;
          }
        }
      }

      // Sum fleet-adjusted capacity for this year
      for (let m = 0; m < 12; m++) {
        const fleet = Math.max(1, activeCarsPerMonth[m]);
        capacityTotal += daysInMonth(y, m) * fleet;
      }
    }

    const demandPct = capacityTotal ? (bookedDays / capacityTotal) * 100 : 0;
    const avgDaily = bookedDays ? earnings / bookedDays : 0;
    return { year: "ALL", trips, bookedDays, earnings, demandPct, avgDaily };
  }

  const monthData = useMemo(() => {
    if (year === "ALL") return combineMonthsAllYears(carKey);
    return byMonth.get(carKey)?.get(year) ?? [];
  }, [byMonth, carKey, year]);

  const yearData = useMemo(() => {
    if (year === "ALL") return combineYearAllYears(carKey);
    return yearly.get(carKey)?.get(year) ?? null;
  }, [yearly, carKey, year]);

  if (!rows || rows.length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 py-12 text-center text-gray-600">
        No trips found in your Profile yet. Upload a
        “trip_earnings_export_*.csv” (or any <code>.csv</code>) under Profile in
        the top-right.
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-6">
      <div className="text-center mb-6">
        <p className="text-gray-600 max-w-2xl mx-auto">
          Month-by-month utilization (demand), trips, booked days, earnings, and
          ADR — plus yearly aggregate. Switch to “All (lifetime)” to see totals
          across years. Update with fresh data by uploading the latest earnings
          under Profile in the top-right.
        </p>
        <div className="h-4" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-end sm:justify-between">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Car</label>
            <select
              className="border rounded-md px-3 py-2 text-sm"
              value={carKey}
              onChange={(e) => setCarKey(e.target.value)}
            >
              {cars.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.name === "All Cars"
                    ? "All Cars"
                    : `${c.name} (${
                        c.id && c.id !== "unknown" ? c.id : "no plate"
                      })`}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Year</label>
            <select
              className="border rounded-md px-3 py-2 text-sm"
              value={year}
              onChange={(e) => {
                const v = e.target.value;
                setYear(v === "ALL" ? "ALL" : Number(v));
              }}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y === "ALL" ? "All (lifetime)" : y}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mt-2 text-center">
        <span className="text-xs text-gray-500">
          {year === "ALL" ? "Lifetime totals across all years" : `Year ${year}`}
        </span>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
        <KPI title="Trips" value={fmtInt(yearData?.trips)} />
        <KPI title="Booked Days" value={fmtInt(yearData?.bookedDays)} />
        <KPI title="Demand" value={fmtPct(yearData?.demandPct)} />
        <KPI title="Earnings" value={fmtUsd(yearData?.earnings)} />
        <KPI title="Avg Daily Rate" value={fmtUsd(yearData?.avgDaily)} />
      </div>

      <div className="mt-8 overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <Th>Month</Th>
              <Th className="text-right">Trips</Th>
              <Th className="text-right">Booked Days</Th>
              <Th className="text-right">Demand</Th>
              <Th className="text-right">Earnings</Th>
              <Th className="text-right">Avg Daily Rate</Th>
            </tr>
          </thead>
          <tbody>
            {monthData.map((m) => (
              <tr key={m.month} className="border-t">
                <Td>{monthLabel(m.month)}</Td>
                <Td className="text-right">{fmtInt(m.trips)}</Td>
                <Td className="text-right">{fmtInt(m.bookedDays)}</Td>
                <Td className="text-right">{fmtPct(m.demandPct)}</Td>
                <Td className="text-right">{fmtUsd(m.earnings)}</Td>
                <Td className="text-right">{fmtUsd(m.avgDaily)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500 mt-3 text-center">
        Demand% = Booked days / Calendar days in month (fleet-adjusted for “All
        Cars”). Avg Daily Rate = Earnings / Booked days.
      </p>

      {/* 🚗 NEW: Fleet Performance Breakdown */}
      <FleetBreakdown rows={rows} year={year} carKeySelected={carKey} />
    </div>
  );
}

/* ==================== Storage Helpers (no CORS ‘fetch’) =================== */
function explainStorageError(err, where = "") {
  const code = err?.code || "unknown";
  const msg = err?.message || "";
  const srv = err?.serverResponse
    ? ` serverResponse=${err.serverResponse}`
    : "";
  return new Error(`${where} [${code}] ${msg}${srv}`);
}

export async function getLatestTripCsvRef(uid) {
  const storage = getStorage(
    undefined,
    "gs://diviseed-3b654.firebasestorage.app"
  );
  const fileRef = ref(storage, `golden/${uid}/transactions.csv`);
  try {
    await getMetadata(fileRef);
  } catch (e) {
    throw (
      explainStorageError?.(
        e,
        "Your golden dataset isn't ready yet. Upload a Turo CSV to generate it."
      ) ??
      new Error(
        "Your golden dataset isn't ready yet. Upload a Turo CSV to generate it."
      )
    );
  }
  console.debug("Using GOLDEN CSV:", fileRef.fullPath);
  return fileRef;
}

async function loadTripsCsvForUser(uid) {
  try {
    const fileRef = await getLatestTripCsvRef(uid);

    const meta = await getMetadata(fileRef).catch((e) => {
      throw explainStorageError(
        e,
        `Reading metadata for ${fileRef.fullPath} failed.`
      );
    });
    console.debug("CSV metadata:", {
      path: fileRef.fullPath,
      size: meta.size,
      contentType: meta.contentType,
      updated: meta.updated,
    });

    const blob = await getBlob(fileRef).catch((e) => {
      throw explainStorageError(e, `Downloading ${fileRef.fullPath} failed.`);
    });

    const csvText = (await blob.text()).replace(/^\uFEFF/, "");
    if (!csvText.trim())
      throw new Error(`Downloaded empty CSV at ${fileRef.fullPath}`);

    const parsed = Papa.parse(csvText, {
      header: true,
      dynamicTyping: false,
      skipEmptyLines: "greedy",
      transformHeader: (h) =>
        String(h || "")
          .replace(/^\uFEFF/, "")
          .trim()
          .toLowerCase()
          .replace(/[^\w]+/g, "_"),
    });
    if (parsed.errors?.length)
      console.warn("CSV parse warnings:", parsed.errors.slice(0, 3));

    const rowsRaw = parsed.data || [];

    // helpers
    const toISO = (v) => {
      if (!v) return "";
      let s = String(v).trim();
      if (/^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?$/u.test(s)) {
        s = s.replace(" ", "T");
        if (/^\d{4}-\d{2}-\d{2}$/u.test(s)) s += "T00:00:00";
        if (!/[zZ]|[+\-]\d{2}:\d{2}$/.test(s)) s += "Z";
        return s;
      }
      return s;
    };
    const toCurrency = (v) => {
      if (v === null || v === undefined) return 0;
      let s = String(v).trim();
      const isParenNeg = /^\(\s*[\s\S]*\s*\)$/.test(s);
      if (isParenNeg) s = s.replace(/[()]/g, "");
      s = s.replace(/[^0-9.\-]/g, "");
      let n = Number(s);
      if (!Number.isFinite(n)) n = 0;
      return isParenNeg ? -n : n;
    };
    const pickKey = (keys, patterns) => {
      for (const p of patterns) {
        const hit = keys.find((k) => p.test(k));
        if (hit) return hit;
      }
      return null;
    };

    const sampleKeys = Object.keys(rowsRaw[0] || {});
    const plateKey =
      pickKey(sampleKeys, [
        /^license_?plate(_number|_no)?$/i,
        /^vehicle_?license_?plate(_number|_no)?$/i,
        /^plate(_number|_no|_num)?$/i,
        /^number_?plate$/i,
        /^registration(_plate|_number|_no)?$/i,
        /^reg(_plate|_number|_no)?$/i,
        /^tag(_number|_no)?$/i,
        /^lpn$/i,
      ]) || null;

    const earningsKey =
      pickKey(sampleKeys, [
        /^(host_)?earn(ing)?s(_.*)?$/i,
        /^host.*payout/i,
        /^payout(_.*)?$/i,
        /^net_?payout/i,
        /^revenue$/i,
        /^amount(_paid)?$/i,
        /^total(_earnings|_payout|_revenue)?$/i,
      ]) || "host_earnings";

    const startKey =
      pickKey(sampleKeys, [
        /^start_time$/,
        /^trip_start$/,
        /^start(_date)?$/,
      ]) || "start_time";
    const endKey =
      pickKey(sampleKeys, [/^end_time$/, /^trip_end$/, /^end(_date)?$/]) ||
      "end_time";
    const nameKey =
      pickKey(sampleKeys, [/^vehicle_name$/, /^car_name$/, /^vehicle$/]) ||
      "vehicle_name";
    const idKey =
      pickKey(sampleKeys, [
        /^vehicle_id$/,
        /^car_id$/,
        /^listing_id$/,
        /^id$/,
      ]) || "vehicle_id";

    // PASS 1
    const firstPass = rowsRaw
      .map((r) => {
        const start_time =
          r[startKey] ?? r.check_out_time ?? r.check_out_date ?? "";
        const end_time = r[endKey] ?? r.check_in_time ?? r.check_in_date ?? "";
        const vehicle_name = r[nameKey];
        const listing_id = r[idKey];

        const fromHeader = plateKey ? r[plateKey] : "";
        const fromName = extractPlateFromVehicleName(vehicle_name);
        const fromAny = extractPlateFromAnyCell(r);
        const plate = normalizePlate(fromHeader || fromName || fromAny);

        return {
          ...r,
          start_time: toISO(start_time),
          end_time: toISO(end_time),
          vehicle_name,
          __listing_id: listing_id ?? "",
          __plate: plate,
          host_earnings: toCurrency(r[earningsKey]),
        };
      })
      .filter(
        (r) => !isNaN(new Date(r.start_time)) && !isNaN(new Date(r.end_time))
      );

    // Best plate per group
    const plateByGroup = new Map();
    const countsByGroup = new Map();
    for (const r of firstPass) {
      const group =
        (r.__listing_id && `LID:${r.__listing_id}`) ||
        (r.vehicle_name && `NAME:${r.vehicle_name}`) ||
        null;
      if (!group) continue;
      if (!countsByGroup.has(group)) countsByGroup.set(group, new Map());
      if (r.__plate) {
        const m = countsByGroup.get(group);
        m.set(r.__plate, (m.get(r.__plate) || 0) + 1);
      }
    }
    for (const [group, m] of countsByGroup.entries()) {
      let best = "",
        bestCnt = -1;
      for (const [plate, cnt] of m.entries()) {
        if (cnt > bestCnt) {
          bestCnt = cnt;
          best = plate;
        }
      }
      if (best) plateByGroup.set(group, best);
    }

    // PASS 2: finalize vehicle_id
    const norm = firstPass.map((r) => {
      const group =
        (r.__listing_id && `LID:${r.__listing_id}`) ||
        (r.vehicle_name && `NAME:${r.vehicle_name}`) ||
        null;
      const bestPlate = r.__plate || (group && plateByGroup.get(group)) || "";
      const fallbackId = r.__listing_id ? String(r.__listing_id).trim() : "";
      const vehicle_id = bestPlate || fallbackId || "unknown";
      const out = { ...r, vehicle_id };
      delete out.__plate;
      delete out.__listing_id;
      return out;
    });

    return norm;
  } catch (e) {
    throw new Error(
      `Please ensure you claimed your Turo Profile under Profile in the top right.`
    );
  }
}

/* ========================= Storage Loader Wrapper ======================== */
function HostDashboardFromStorage() {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("idle");
  const [rows, setRows] = useState([]);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u || null)), []);

  useEffect(() => {
    if (!user) {
      setStatus("unauth");
      return;
    }
    (async () => {
      try {
        setStatus("loading");
        const data = await loadTripsCsvForUser(user.uid);
        if (!data.length) setStatus("empty");
        else {
          setRows(data);
          setStatus("ready");
        }
      } catch (e) {
        console.error("CSV load error:", e);
        setErrMsg(e.message);
        setStatus("error");
      }
    })();
  }, [user]);

  if (status === "unauth")
    return (
      <div className="w-full max-w-4xl mx-auto px-4 py-12 text-center text-gray-600">
        Please log in to view your dashboard.
      </div>
    );
  if (status === "loading")
    return (
      <div className="w-full max-w-4xl mx-auto px-4 py-12 text-center text-gray-600">
        Loading your report…
      </div>
    );
  if (status === "empty") {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 py-12 text-center text-gray-600">
        No CSV found in <code>reports/{user?.uid}</code>. Upload{" "}
        <code className="mx-1">trip_earnings_export_*.csv</code> (or any{" "}
        <code>.csv</code>) and refresh.
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-red-600 font-medium">
          Couldn’t load your Business Insights report
        </p>
        <p className="text-gray-600 mt-2">{errMsg}</p>
      </div>
    );
  }

  return <Dashboard rows={rows} />;
}

/* ============================== Modal Wrapper ============================ */
export default function HostDashboard() {
  const [showDashboard, setShowDashboard] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setShowDashboard(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    function onClick(e) {
      if (
        showDashboard &&
        dialogRef.current &&
        !dialogRef.current.contains(e.target)
      ) {
        setShowDashboard(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showDashboard]);

  return (
    <div className="w-full text-center p-4 text-sm relative">
      <button
        onClick={() => setShowDashboard(!showDashboard)}
        className="text-blue-600 underline"
      >
        Host Business Insights Dashboard
      </button>

      {showDashboard && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" />
          <div className="fixed left-1/2 top-16 -translate-x-1/2 z-50 w-[92vw] max-w-4xl">
            <div
              ref={dialogRef}
              className="relative bg-white shadow-lg border rounded p-6 text-left overflow-y-auto max-h-[80vh]"
            >
              <button
                onClick={() => setShowDashboard(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-black text-lg"
                aria-label="Close"
              >
                ×
              </button>

              <h2 className="text-2xl font-bold mb-4 text-center">
                Host Business Insights Dashboard
              </h2>
              <HostDashboardFromStorage />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
